import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import '../../../../css/flow/authorization-config.less';
import '../../../../css/flow/shared-elements.less';
import { getIdentityProviderList } from '../../sources/identityprovider';
import { getAllServices } from '../../sources/service';
import type { GroupAuthorizationAPI } from '../../types/flow';
import type { FlowIdentityProviderAPI, IdentityProviderAPI } from '../../types/identityProvider';
import type { ServiceElementResponseAPI } from '../../types/service';
import Loader from '../loader/Loader';
import Connector from './Connector';
import IdentityProvider from './IdentityProvider';
import { useNotificationDispatch } from '../notifications/NotificationProvider';

type TabName = 'idp' | 'connector';

interface Props {
    identityProvider: FlowIdentityProviderAPI | null;
    onChangeIdentityProvider: (identityProvider: FlowIdentityProviderAPI | null) => void;
    authorization: GroupAuthorizationAPI | null;
    onChangeAuthorization: (authorization: GroupAuthorizationAPI) => void;
    modalContainer: HTMLElement | null;
}

const Authorization = ({
    identityProvider,
    onChangeIdentityProvider,
    authorization,
    onChangeAuthorization,
    modalContainer,
}: Props) => {
    const [activeTab, setActiveTab] = useState<TabName>('idp');
    const dispatchNotification = useNotificationDispatch();

    const [identityProviders, setIdentityProviders] = useState<IdentityProviderAPI[]>([]);
    const [isLoadingIdentityProviders, setIsLoadingIdentityProviders] = useState(true);

    const [connectors, setConnectors] = useState<ServiceElementResponseAPI[]>([]);
    const [isLoadingConnectors, setIsLoadingConnectors] = useState(false);

    useEffect(() => {
        const fetchIDPList = async () => {
            setIsLoadingIdentityProviders(true);
            const identityProviderResponse = await getIdentityProviderList({
                limit: 1000000,
            });
            setIdentityProviders(identityProviderResponse.items);
            setIsLoadingIdentityProviders(false);
        };

        fetchIDPList().catch((error) => {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        });
    }, [dispatchNotification]);

    useEffect(() => {
        const fetchConnectorList = async () => {
            setIsLoadingConnectors(true);
            const connectors = await getAllServices();
            setConnectors(connectors);
            setIsLoadingConnectors(false);
        };

        fetchConnectorList().catch((error) => {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        });
    }, [dispatchNotification]);

    return (
        <div className="authorization-config">
            <h2>Authentication</h2>
            <ul className="nav nav-tabs">
                <li className={classNames({ active: activeTab === 'idp' })}>
                    <button
                        className="nav-tab"
                        onClick={() => setActiveTab('idp')}
                        title="Identity Provider"
                        role="tab"
                        name="Identity Provider"
                        type="button"
                    >
                        Identity Provider
                    </button>
                </li>
                <li className={classNames({ active: activeTab === 'connector' })}>
                    <button
                        className="nav-tab"
                        onClick={() => setActiveTab('connector')}
                        title="Connector"
                        role="tab"
                        name="Identity Connector"
                        type="button"
                    >
                        Connector
                    </button>
                </li>
            </ul>
            {activeTab === 'idp' &&
                (isLoadingIdentityProviders ? (
                    <Loader />
                ) : (
                    <IdentityProvider
                        identityProvider={identityProvider}
                        providers={identityProviders}
                        onChangeIdentityProvider={onChangeIdentityProvider}
                        modalContainer={modalContainer}
                    />
                ))}
            {activeTab === 'connector' &&
                (isLoadingConnectors ? (
                    <Loader />
                ) : (
                    <Connector
                        authorization={authorization}
                        connectors={connectors}
                        onChangeAuthorization={onChangeAuthorization}
                    />
                ))}
        </div>
    );
};

export default memo(Authorization);
